import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Check } from "lucide-react";
import { useNotifications } from "@/contexts/NotificationsContext";

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
}

const TYPE_EMOJI = {
    match: "🎉",
    message: "💬",
    likes_today: "👀",
    ghost: "💀",
    new_flat: "🏠",
    nudge: "🫶",
};

export default function NotificationBell() {
    const [open, setOpen] = useState(false);
    const { items, unread, markRead, markAllRead, refresh } = useNotifications();
    const navigate = useNavigate();
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        refresh({ silent: false });
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open, refresh]);

    const onItemClick = async (n) => {
        if (!n.read) markRead(n.id);
        setOpen(false);
        if (n.url) navigate(n.url);
    };

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid="notif-bell-btn"
                aria-label="Notifications"
                className="relative h-10 w-10 rounded-full grid place-items-center border-2 border-foreground bg-card hover:bg-accent transition"
            >
                <Bell className="h-5 w-5" strokeWidth={2.5} />
                {unread > 0 && (
                    <span
                        data-testid="notif-unread-badge"
                        className="absolute -top-1 -right-1 min-w-[20px] h-[20px] rounded-full bg-primary text-primary-foreground text-[10px] font-black grid place-items-center px-1 border-2 border-background"
                    >
                        {unread > 99 ? "99+" : unread}
                    </span>
                )}
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.98 }}
                        transition={{ duration: 0.15 }}
                        data-testid="notif-panel"
                        className="absolute right-0 mt-2 w-[min(92vw,380px)] max-h-[70vh] flex flex-col rounded-3xl border-2 border-foreground bg-card shadow-[0_20px_50px_-12px_rgba(0,0,0,0.35)] overflow-hidden z-50"
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b-2 border-foreground bg-background">
                            <div className="font-display font-black text-lg">Vibe check</div>
                            {items.some((n) => !n.read) && (
                                <button
                                    onClick={markAllRead}
                                    data-testid="notif-mark-all-btn"
                                    className="text-[11px] font-black uppercase tracking-widest text-primary hover:underline flex items-center gap-1"
                                >
                                    <Check className="h-3 w-3" strokeWidth={3} /> Mark all
                                </button>
                            )}
                        </div>

                        <div className="overflow-y-auto flex-1">
                            {items.length === 0 ? (
                                <div className="p-8 text-center text-sm text-muted-foreground">
                                    <div className="text-3xl mb-2">🌙</div>
                                    Quiet here. New activity will land here.
                                </div>
                            ) : (
                                <ul>
                                    {items.map((n) => (
                                        <li key={n.id}>
                                            <button
                                                onClick={() => onItemClick(n)}
                                                data-testid={`notif-item-${n.type}`}
                                                className={`w-full text-left px-4 py-3 border-b border-border last:border-b-0 flex gap-3 items-start hover:bg-accent transition ${
                                                    !n.read ? "bg-accent/40" : ""
                                                }`}
                                            >
                                                <div className="text-2xl flex-shrink-0 mt-0.5">
                                                    {TYPE_EMOJI[n.type] || "✨"}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <div className="font-bold text-sm truncate">{n.title}</div>
                                                        <div className="text-[10px] text-muted-foreground font-semibold flex-shrink-0">
                                                            {timeAgo(n.created_at)}
                                                        </div>
                                                    </div>
                                                    <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                                                        {n.body}
                                                    </div>
                                                </div>
                                                {!n.read && (
                                                    <div className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-2" />
                                                )}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
